import { c as _c } from "react/compiler-runtime";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { Box, Button, Chip, Typography } from "@mui/material";
import React, { useMemo, useState } from "react";
import { categoryLabels } from "#/entities/category/model/categoryLabels";
import type { ProjectCategory } from "#/server/db/generated/enums";
import type { RouterOutputs } from "#/shared/api";
import { useProjectBrowserContext } from "./ProjectBrowserContext";
type ProjectBrief = RouterOutputs["project"]["allBrief"][number];
type ProjectBrowserCategoryBarProps = {
  projects: ProjectBrief[] | undefined;
};

// Max height for collapsed state (slightly taller to peek second row with fade)
const COLLAPSED_MAX_HEIGHT = 64; // Single row + peek of second

export const ProjectBrowserCategoryBar: React.FC<ProjectBrowserCategoryBarProps> = t0 => {
  const $ = _c(51);
  const {
    projects
  } = t0;
  const {
    selectedCategories,
    setSelectedCategories
  } = useProjectBrowserContext();
  const [isExpanded, setIsExpanded] = useState(false);
  let t1;
  bb0: {
    if (!projects) {
      let t2;
      if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = new Map();
        $[0] = t2;
      } else {
        t2 = $[0];
      }
      t1 = t2;
      break bb0;
    }
    let counts;
    if ($[1] !== projects) {
      counts = new Map();
      for (const project of projects) {
        const currentCount = counts.get(project.category) || 0;
        counts.set(project.category, currentCount + 1);
      }
      $[1] = projects;
      $[2] = counts;
    } else {
      counts = $[2];
    }
    t1 = counts;
  }
  const categoryCounts = t1;
  let t2;
  if ($[3] !== categoryCounts) {
    const allCategories = Array.from(categoryCounts.keys());
    t2 = allCategories.sort((categoryLeft, categoryRight) => {
      const countLeft = categoryCounts.get(categoryLeft) || 0;
      const countRight = categoryCounts.get(categoryRight) || 0;
      if (countLeft !== countRight) {
        return countRight - countLeft;
      }
      return categoryLabels[categoryLeft].localeCompare(categoryLabels[categoryRight]);
    });
    $[3] = categoryCounts;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const sortedCategories = t2;
  let t3;
  if ($[5] !== selectedCategories || $[6] !== setSelectedCategories) {
    t3 = category => {
      const isSelected = selectedCategories.includes(category);
      if (isSelected) {
        setSelectedCategories(selectedCategories.filter(selectedCategory => selectedCategory !== category));
      } else {
        setSelectedCategories([...selectedCategories, category]);
      }
    };
    $[5] = selectedCategories;
    $[6] = setSelectedCategories;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const handleCategoryClick = t3;
  let t4;
  if ($[8] !== handleCategoryClick) {
    t4 = (ev, category_0) => {
      if (ev.key === "Enter" || ev.key === " ") {
        ev.preventDefault();
        handleCategoryClick(category_0);
      }
    };
    $[8] = handleCategoryClick;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const handleKeyDown = t4;
  if (sortedCategories.length === 0) {
    return null;
  }
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      borderBottom: 1,
      borderColor: "divider"
    };
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      px: 2,
      pt: 1.5,
      pb: 1
    };
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Box sx={t6}><Typography variant="subtitle2" component="h3" sx={{
        fontWeight: 600,
        color: "text.secondary",
        fontSize: "0.75rem",
        textTransform: "uppercase",
        letterSpacing: "0.5px"
      }}>Categories</Typography></Box>;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      px: 1.5
    };
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      position: "relative"
    };
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const t10 = isExpanded ? "none" : COLLAPSED_MAX_HEIGHT;
  const t11 = isExpanded ? "visible" : "hidden";
  let t12;
  if ($[15] !== t10 || $[16] !== t11) {
    t12 = {
      display: "flex",
      flexWrap: "wrap",
      gap: 1,
      maxHeight: t10,
      overflowY: t11,
      transition: "max-height 0.3s ease-in-out"
    };
    $[15] = t10;
    $[16] = t11;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== categoryCounts || $[19] !== handleCategoryClick || $[20] !== handleKeyDown || $[21] !== selectedCategories || $[22] !== sortedCategories) {
    let t14;
    if ($[24] !== categoryCounts || $[25] !== handleCategoryClick || $[26] !== handleKeyDown || $[27] !== selectedCategories) {
      t14 = category_1 => {
        const count = categoryCounts.get(category_1) || 0;
        const isSelected_0 = selectedCategories.includes(category_1);
        const label = categoryLabels[category_1] ?? String(category_1);
        return <Chip key={category_1} label={`${label}: ${count}`} onClick={() => handleCategoryClick(category_1)} onKeyDown={ev_0 => handleKeyDown(ev_0, category_1)} color={isSelected_0 ? "primary" : "default"} variant={isSelected_0 ? "filled" : "outlined"} sx={{
          cursor: "pointer",
          fontWeight: isSelected_0 ? 600 : 400,
          "&:focus-visible": {
            outline: "2px solid",
            outlineColor: "primary.main",
            outlineOffset: 2
          }
        }} tabIndex={0} aria-pressed={isSelected_0} aria-label={`Filter by ${label} category, ${count} projects`} />;
      };
      $[24] = categoryCounts;
      $[25] = handleCategoryClick;
      $[26] = handleKeyDown;
      $[27] = selectedCategories;
      $[28] = t14;
    } else {
      t14 = $[28];
    }
    t13 = sortedCategories.map(t14);
    $[18] = categoryCounts;
    $[19] = handleCategoryClick;
    $[20] = handleKeyDown;
    $[21] = selectedCategories;
    $[22] = sortedCategories;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[29] !== t12 || $[30] !== t13) {
    t14 = <Box sx={t12}>{t13}</Box>;
    $[29] = t12;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== isExpanded) {
    t15 = !isExpanded && <Box sx={{
      position: "absolute",
      bottom: 0,
      left: 0,
      right: 0,
      height: 32,
      background: _temp,
      pointerEvents: "auto"
    }} aria-hidden={true} />;
    $[32] = isExpanded;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== t14 || $[35] !== t15) {
    t16 = <Box sx={t9}>{t14}{t15}</Box>;
    $[34] = t14;
    $[35] = t15;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = {
      display: "flex",
      justifyContent: "flex-end"
    };
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  let t19;
  if ($[38] !== isExpanded) {
    t18 = () => setIsExpanded(!isExpanded);
    t19 = isExpanded ? <ExpandLess /> : <ExpandMore />;
    $[38] = isExpanded;
    $[39] = t18;
    $[40] = t19;
  } else {
    t18 = $[39];
    t19 = $[40];
  }
  let t20;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = {
      textTransform: "none",
      minWidth: "auto",
      px: 1
    };
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  const t21 = isExpanded ? "Collapse categories" : "Expand categories";
  const t22 = isExpanded ? "Collapse" : "Show more";
  let t23;
  if ($[42] !== isExpanded || $[43] !== t18 || $[44] !== t19 || $[45] !== t21 || $[46] !== t22) {
    t23 = <Box sx={t17}><Button onClick={t18} endIcon={t19} size="small" sx={t20} aria-label={t21} aria-expanded={isExpanded}>{t22}</Button></Box>;
    $[42] = isExpanded;
    $[43] = t18;
    $[44] = t19;
    $[45] = t21;
    $[46] = t22;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  let t24;
  if ($[48] !== t16 || $[49] !== t23) {
    t24 = <Box sx={t5} role="region" aria-label="Category filters">{t7}<Box sx={t8}>{t16}{t23}</Box></Box>;
    $[48] = t16;
    $[49] = t23;
    $[50] = t24;
  } else {
    t24 = $[50];
  }
  return t24;
};
function _temp(theme) {
  return `linear-gradient(to top, ${theme.palette.background.paper}, transparent)`;
}